import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import LegalNav from '../../components/LegalNav';
import '../LegalPage.css';

function UKTaxBandsGuide() {
  useEffect(() => {
    document.title = 'Understanding UK Tax Bands 2025-2026: Complete Guide & Calculator - My Fair Share';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Complete guide to UK tax bands for 2025-2026. Learn about income tax rates, personal allowance, basic rate, higher rate, and additional rate tax bands with examples.');
    }
  }, []);

  return (
    <div className="legal-page">
      <div className="legal-container">
        <Link to="/" className="back-link">← Back to Calculator</Link>
        
        <article>
          <h1>Understanding UK Tax Bands: Complete Guide for 2025-2026</h1>
          <p className="last-updated">Published: December 2025 | Reading time: 10 minutes</p>

          <section>
            <p>
              UK tax bands can seem complicated at first, but understanding them is crucial for knowing how much tax you'll pay 
              and planning your finances effectively. Many people mistakenly believe that moving into a higher tax band means 
              all their income is taxed at that higher rate - but that's not true! This guide will explain exactly how UK tax 
              bands work in 2025-2026 with clear examples.
            </p>
          </section>

          <section>
            <h2>What Are UK Tax Bands?</h2>
            <p>
              UK tax bands (also called tax brackets) divide your income into different portions, each taxed at a different rate. 
              Think of your salary as being split into sections, with each section taxed separately. This is called a 
              "progressive tax system" because the rate increases as your income increases.
            </p>
            <p>
              The key thing to understand: <strong>you never pay the higher rate of tax on all your income</strong> - only on 
              the portion that falls into each band.
            </p>
          </section>

          <section>
            <h2>UK Tax Bands 2025-2026: The Complete List</h2>
            <p>
              Here are the current UK income tax bands for England, Wales, and Northern Ireland for the 2025-2026 tax year:
            </p>

            <h3>1. Personal Allowance: £0 - £12,570 (0% Tax)</h3>
            <p>
              <strong>What it is:</strong> The amount you can earn completely tax-free<br />
              <strong>Tax rate:</strong> 0%<br />
              <strong>Who gets it:</strong> Almost everyone, though it reduces if you earn over £100,000
            </p>
            <p>
              This is your tax-free allowance. Whether you earn £15,000 or £150,000, the first £12,570 is not taxed 
              (unless you're in the high-earner taper zone).
            </p>

            <h3>2. Basic Rate Tax Band: £12,571 - £50,270 (20% Tax)</h3>
            <p>
              <strong>Income range:</strong> £12,571 to £50,270<br />
              <strong>Tax rate:</strong> 20%<br />
              <strong>Taxable amount in band:</strong> Up to £37,700
            </p>
            <p>
              This is where most UK workers pay tax. You pay 20% income tax only on the portion of your salary that falls 
              within this range.
            </p>

            <h3>3. Higher Rate Tax Band: £50,271 - £125,140 (40% Tax)</h3>
            <p>
              <strong>Income range:</strong> £50,271 to £125,140<br />
              <strong>Tax rate:</strong> 40%<br />
              <strong>Taxable amount in band:</strong> Up to £74,870
            </p>
            <p>
              You only pay 40% tax on income above £50,270. Everything below this is still taxed at the lower rates.
            </p>

            <h3>4. Additional Rate Tax Band: Over £125,140 (45% Tax)</h3>
            <p>
              <strong>Income range:</strong> Above £125,140<br />
              <strong>Tax rate:</strong> 45%<br />
              <strong>Note:</strong> At this income level, you've also lost your entire personal allowance
            </p>
            <p>
              The highest tax band in the UK. Only about 1% of taxpayers reach this level.
            </p>
          </section>

          <section>
            <h2>How UK Tax Bands Actually Work: Real Examples</h2>
            <p>
              Let's look at real examples to show exactly how tax bands work in practice:
            </p>

            <h3>Example 1: £25,000 Salary (Basic Rate Only)</h3>
            <p>
              <strong>Sarah earns £25,000 per year. How much tax does she pay?</strong>
            </p>
            <ul>
              <li><strong>Personal Allowance (0%):</strong> First £12,570 = £0 tax</li>
              <li><strong>Basic Rate (20%):</strong> Remaining £12,430 × 20% = £2,486 tax</li>
              <li><strong>Total Income Tax:</strong> £2,486</li>
              <li><strong>Take-home (before NI):</strong> £22,514</li>
            </ul>
            <p>
              Sarah is a basic rate taxpayer, paying an overall tax rate of 9.94% on her total salary.
            </p>

            <h3>Example 2: £60,000 Salary (Higher Rate Taxpayer)</h3>
            <p>
              <strong>James earns £60,000 per year. How is his tax calculated?</strong>
            </p>
            <ul>
              <li><strong>Personal Allowance (0%):</strong> First £12,570 = £0 tax</li>
              <li><strong>Basic Rate (20%):</strong> £12,571 to £50,270 (£37,700) × 20% = £7,540 tax</li>
              <li><strong>Higher Rate (40%):</strong> £50,271 to £60,000 (£9,730) × 40% = £3,892 tax</li>
              <li><strong>Total Income Tax:</strong> £11,432</li>
              <li><strong>Take-home (before NI):</strong> £48,568</li>
            </ul>
            <p>
              Notice that James doesn't pay 40% tax on all £60,000 - only on the £9,730 above £50,270. His overall tax rate 
              is 19.05% on his total salary.
            </p>

            <h3>Example 3: £150,000 Salary (Additional Rate)</h3>
            <p>
              <strong>Emma earns £150,000 per year. How does her tax work across all bands?</strong>
            </p>
            <ul>
              <li><strong>Personal Allowance:</strong> £0 (completely tapered away at this income)</li>
              <li><strong>Personal Allowance tax recovery (20%):</strong> £12,570 × 20% = £2,514 tax</li>
              <li><strong>Basic Rate (20%):</strong> £37,700 × 20% = £7,540 tax</li>
              <li><strong>Higher Rate (40%):</strong> £74,870 × 40% = £29,948 tax</li>
              <li><strong>Additional Rate (45%):</strong> £24,860 × 45% = £11,187 tax</li>
              <li><strong>Total Income Tax:</strong> £51,189</li>
              <li><strong>Take-home (before NI):</strong> £98,811</li>
            </ul>
            <p>
              Emma pays an overall tax rate of 34.13% on her total salary, despite being in the 45% additional rate band.
            </p>
          </section>

          <section>
            <h2>The £100,000 Personal Allowance Taper Trap</h2>
            <p>
              One of the most confusing aspects of UK tax bands is the personal allowance taper for high earners. Here's what happens:
            </p>

            <h3>How the Taper Works</h3>
            <p>
              If you earn more than £100,000, your personal allowance reduces by £1 for every £2 you earn above £100,000. 
              This continues until your personal allowance reaches zero at £125,140.
            </p>

            <h3>The 60% Tax Trap</h3>
            <p>
              Between £100,000 and £125,140, you effectively pay a marginal tax rate of 60%:
            </p>
            <ul>
              <li>40% higher rate income tax on the additional income</li>
              <li>Plus another 20% because you're losing £1 of personal allowance (which would have been taxed at 20%) for every £2 earned</li>
              <li>40% + 20% = 60% effective marginal rate</li>
            </ul>

            <h3>Example: £110,000 vs £100,000</h3>
            <p>
              <strong>Person A earns £100,000:</strong>
            </p>
            <ul>
              <li>Personal Allowance: £12,570</li>
              <li>Total Income Tax: £27,432</li>
              <li>Take-home: £68,558 (before NI)</li>
            </ul>

            <p>
              <strong>Person B earns £110,000:</strong>
            </p>
            <ul>
              <li>Personal Allowance: £7,570 (reduced by £5,000)</li>
              <li>Total Income Tax: £33,432</li>
              <li>Take-home: £72,558 (before NI)</li>
            </ul>

            <p>
              Person B earns £10,000 more but only takes home £4,000 extra - the effective tax on that £10,000 is 60%!
            </p>

            <h3>Tax Planning Around £100,000</h3>
            <p>
              If you earn close to £100,000, consider:
            </p>
            <ul>
              <li>Making pension contributions to reduce your adjusted net income below £100,000</li>
              <li>Salary sacrifice schemes (childcare vouchers, cycle to work)</li>
              <li>Charitable donations (which reduce taxable income)</li>
            </ul>
            <p>
              These strategies can save you significant tax by keeping your income below the £100,000 threshold.
            </p>
          </section>

          <section>
            <h2>Common Misconceptions About Tax Bands</h2>

            <h3>Myth 1: "If I get a raise into the next tax band, I'll take home less money"</h3>
            <p>
              <strong>False!</strong> You always take home more when you earn more. Only the portion above the threshold is 
              taxed at the higher rate. The exception is the £100,000-£125,140 range where the effective rate is 60%, but 
              even then, you still take home more overall.
            </p>

            <h3>Myth 2: "Higher rate taxpayers pay 40% tax on everything"</h3>
            <p>
              <strong>False!</strong> Higher rate taxpayers still get their personal allowance tax-free (unless earning over 
              £100,000) and pay 20% on income in the basic rate band. Only income above £50,270 is taxed at 40%.
            </p>

            <h3>Myth 3: "Tax bands are the same across the UK"</h3>
            <p>
              <strong>Partially false!</strong> Scotland has different income tax rates and bands. The tax bands described 
              here apply to England, Wales, and Northern Ireland only.
            </p>

            <h3>Myth 4: "I can choose which tax band to be in"</h3>
            <p>
              <strong>False!</strong> Your tax band is determined automatically by your income. You can't opt in or out, 
              though you can reduce your taxable income through legal means like pension contributions.
            </p>
          </section>

          <section>
            <h2>National Insurance: The Other Bands You Need to Know</h2>
            <p>
              In addition to income tax bands, you also pay National Insurance contributions with their own bands:
            </p>

            <h3>National Insurance Bands 2025-2026</h3>
            <ul>
              <li><strong>£0 - £12,570:</strong> 0% NI</li>
              <li><strong>£12,571 - £50,270:</strong> 8% NI</li>
              <li><strong>Above £50,270:</strong> 2% NI</li>
            </ul>

            <h3>Total Deduction Rates (Income Tax + NI)</h3>
            <p>
              When you combine income tax and National Insurance, here are the total deduction rates:
            </p>
            <ul>
              <li><strong>£0 - £12,570:</strong> 0% (personal allowance)</li>
              <li><strong>£12,571 - £50,270:</strong> 28% total (20% tax + 8% NI)</li>
              <li><strong>£50,271 - £100,000:</strong> 42% total (40% tax + 2% NI)</li>
              <li><strong>£100,000 - £125,140:</strong> 62% total (60% effective tax + 2% NI)</li>
              <li><strong>Above £125,140:</strong> 47% total (45% tax + 2% NI)</li>
            </ul>
          </section>

          <section>
            <h2>How to Find Your Tax Band</h2>
            <p>
              Working out your tax band is simple:
            </p>

            <h3>Method 1: Use a Tax Calculator</h3>
            <p>
              The easiest way is to use <Link to="/">our free UK tax calculator</Link>. Simply enter your salary and you'll 
              instantly see:
            </p>
            <ul>
              <li>Which tax band(s) you fall into</li>
              <li>Exactly how much tax you pay in each band</li>
              <li>Your total income tax and National Insurance</li>
              <li>Your monthly and annual take-home pay</li>
            </ul>

            <h3>Method 2: Check Your Gross Salary</h3>
            <p>
              Based on your gross annual salary:
            </p>
            <ul>
              <li><strong>Up to £12,570:</strong> No tax (within personal allowance)</li>
              <li><strong>£12,571 - £50,270:</strong> Basic rate taxpayer</li>
              <li><strong>£50,271 - £125,140:</strong> Higher rate taxpayer</li>
              <li><strong>Above £125,140:</strong> Additional rate taxpayer</li>
            </ul>

            <h3>Method 3: Look at Your Payslip</h3>
            <p>
              Your payslip shows your tax code. The standard 2025-2026 code is 1257L:
            </p>
            <ul>
              <li><strong>1257L:</strong> Standard personal allowance</li>
              <li><strong>BR:</strong> All income taxed at basic rate (20%)</li>
              <li><strong>D0:</strong> All income taxed at higher rate (40%)</li>
              <li><strong>D1:</strong> All income taxed at additional rate (45%)</li>
            </ul>
          </section>

          <section>
            <h2>Tax Band Changes Over Time</h2>
            <p>
              Tax bands and rates have changed significantly over the years:
            </p>

            <h3>Historical Context</h3>
            <ul>
              <li><strong>2010-2011:</strong> Personal allowance was £6,475</li>
              <li><strong>2015-2016:</strong> Personal allowance increased to £10,600</li>
              <li><strong>2020-2021:</strong> Personal allowance reached £12,500</li>
              <li><strong>2021-2022 onwards:</strong> Personal allowance frozen at £12,570</li>
            </ul>

            <h3>Frozen Thresholds</h3>
            <p>
              The government has frozen income tax thresholds until April 2028. This means:
            </p>
            <ul>
              <li>Personal allowance stays at £12,570</li>
              <li>Higher rate threshold stays at £50,270</li>
              <li>As wages increase with inflation, more people move into higher tax bands</li>
              <li>This is called "fiscal drag" - a stealth tax increase</li>
            </ul>
          </section>

          <section>
            <h2>Scottish Tax Bands: The Difference</h2>
            <p>
              If you live in Scotland, you pay different income tax rates:
            </p>

            <h3>Scottish Income Tax Bands 2025-2026</h3>
            <ul>
              <li><strong>Personal Allowance:</strong> £0 - £12,570 (0%)</li>
              <li><strong>Starter Rate:</strong> £12,571 - £15,397 (19%)</li>
              <li><strong>Basic Rate:</strong> £15,398 - £27,491 (20%)</li>
              <li><strong>Intermediate Rate:</strong> £27,492 - £43,662 (21%)</li>
              <li><strong>Higher Rate:</strong> £43,663 - £125,140 (42%)</li>
              <li><strong>Advanced Rate:</strong> £125,141 - £150,000 (45%)</li>
              <li><strong>Top Rate:</strong> Above £150,000 (48%)</li>
            </ul>
            <p>
              <strong>Note:</strong> Our calculator currently uses UK (England, Wales, Northern Ireland) rates. 
              Scottish taxpayers may see different results.
            </p>
          </section>

          <section>
            <h2>Practical Tips for Managing Tax Bands</h2>

            <h3>1. Maximize Pension Contributions</h3>
            <p>
              Pension contributions reduce your taxable income. If you're close to a tax band threshold, contributing to your 
              pension can:
            </p>
            <ul>
              <li>Keep you in a lower tax band</li>
              <li>Get you 20%, 40%, or 45% tax relief</li>
              <li>Save on National Insurance (if salary sacrifice)</li>
            </ul>

            <h3>2. Understand Bonus Tax</h3>
            <p>
              Bonuses are taxed in the month received, which might push you temporarily into a higher tax band. Your tax code 
              usually adjusts over the year, but it's important to understand the cashflow impact.
            </p>

            <h3>3. Plan Around the £100,000 Threshold</h3>
            <p>
              The 60% effective tax rate between £100,000-£125,140 makes this the most tax-inefficient income range. 
              Consider pension contributions or salary sacrifice to stay below £100,000.
            </p>

            <h3>4. Use Marriage Allowance</h3>
            <p>
              If you're married and one spouse earns under the personal allowance, they can transfer £1,260 of their 
              allowance to their partner (who must be a basic rate taxpayer), saving up to £252 per year.
            </p>
          </section>

          <section>
            <h2>Calculate Your Tax Band Now</h2>
            <p>
              Understanding tax bands is one thing - seeing exactly how they apply to your salary is another. 
              <Link to="/"> Use our free UK tax calculator</Link> to:
            </p>
            <ul>
              <li>See exactly which tax bands your income falls into</li>
              <li>Calculate your precise income tax across all bands</li>
              <li>View your National Insurance contributions</li>
              <li>Find out your true take-home pay</li>
              <li>Explore what-if scenarios</li>
            </ul>
            <p>
              All calculations are instant, private, and based on official HMRC rates for 2025-2026.
            </p>
          </section>

          <section>
            <h2>Conclusion: Tax Bands Made Simple</h2>
            <p>
              UK tax bands create a progressive tax system where higher earners pay more tax, but only on their higher income. 
              The key points to remember:
            </p>
            <ul>
              <li>You never pay the higher rate on all your income - only on the portion in each band</li>
              <li>A pay rise never leaves you worse off (except in extreme edge cases)</li>
              <li>Personal allowance reduces for earners over £100,000</li>
              <li>Income tax bands are frozen until 2028, creating fiscal drag</li>
              <li>Scotland has different tax bands and rates</li>
            </ul>
            <p>
              Armed with this knowledge, you can better understand your payslip, plan your finances, and make informed 
              decisions about your career and tax planning. <Link to="/">Try our calculator</Link> to see how these 
              bands apply to your specific salary.
            </p>
          </section>
        </article>

        <LegalNav />
      </div>
    </div>
  );
}

export default UKTaxBandsGuide;
